import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getRentalSettings, saveRentalSettings } from "../../../api/onboarding";
import { useAuth } from "../../../context/AuthContext";
import {
  publishRentalsFeatureSnapshot,
  useRentalsFeature,
} from "../../../context/RentalsFeatureContext";
import {
  getMarketplaceOperatorHubPath,
  getMarketplaceOperatorWorkspaceCreatePath,
  getMarketplaceOperatorWorkspacesPath,
} from "../../../utils/marketplaceOperatorRoutes";
import RentalsShell, { rentalsHeroSecondaryActionClass } from "./RentalsShell";

const defaultSettings = {
  rental_mode: "internal",
  discoverability: "private",
  allow_public_checkout: false,
  support_email: "",
  support_phone: "",
  public_brand_label: "Ellyanna MyBoothShare",
  hero_headline: "Find premium salon workspaces inside the Ellyanna network.",
  hero_subheadline:
    "Use the same business, salon, and rental platform to manage public discovery, workspace requests, and future renter workflows.",
  marketplace_enabled: false,
  default_salon_id: null,
  default_policy_set_id: null,
  default_booking_mode: "internal",
  default_requires_approval: false,
};

const fieldClass =
  "h-11 w-full min-w-0 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";
const textareaClass =
  "min-h-[120px] w-full min-w-0 rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

function SectionCard({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-6 py-4">
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        {description ? (
          <p className="mt-1 break-words text-sm leading-6 text-slate-500">{description}</p>
        ) : null}
      </div>
      <div className="min-w-0 space-y-5 px-6 py-6">{children}</div>
    </section>
  );
}

function InputLabel({ label, children, hint = "" }) {
  return (
    <label className="block min-w-0 space-y-2">
      <span className="block whitespace-normal break-words text-sm font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {children}
      {hint ? <p className="break-words text-xs leading-5 text-slate-500">{hint}</p> : null}
    </label>
  );
}

function SummaryChip({ label, value, tone = "slate" }) {
  const toneClass =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "rose"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <div className={`min-w-0 rounded-2xl border px-4 py-3 ${toneClass}`}>
      <div className="break-words text-xs font-semibold uppercase tracking-[0.18em]">
        {label}
      </div>
      <div className="mt-1 break-words text-base font-semibold">{value}</div>
    </div>
  );
}

export default function RentalSettingsPage() {
  const { token } = useAuth();
  const rentalsFeature = useRentalsFeature();
  const [form, setForm] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    getRentalSettings(token)
      .then((data) => {
        if (!active) return;
        setForm((prev) => ({
          ...prev,
          ...(data || {}),
        }));
      })
      .catch((err) => {
        if (active) {
          setError(err.message || "Unable to load rental settings.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const summaryCards = useMemo(
    () => [
      {
        label: "Mode",
        value: form.rental_mode === "public" ? "Public marketplace" : form.rental_mode,
        tone: "slate",
      },
      {
        label: "Discoverability",
        value: form.discoverability || "private",
        tone:
          form.discoverability === "public"
            ? "emerald"
            : form.discoverability === "invite_only"
            ? "rose"
            : "slate",
      },
      {
        label: "Checkout",
        value: form.allow_public_checkout ? "Public checkout enabled" : "Approval-led flow",
        tone: form.allow_public_checkout ? "emerald" : "slate",
      },
      {
        label: "Policy Default",
        value: form.default_policy_set_id ? `Policy #${form.default_policy_set_id}` : "Seeded default policy",
        tone: form.default_policy_set_id ? "emerald" : "slate",
      },
    ],
    [form]
  );

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError("");
    setFlash("");
    try {
      const payload = {
        ...form,
        support_email: form.support_email || null,
        support_phone: form.support_phone || null,
        public_brand_label: form.public_brand_label || null,
        hero_headline: form.hero_headline || null,
        hero_subheadline: form.hero_subheadline || null,
        default_salon_id: form.default_salon_id ? Number(form.default_salon_id) : null,
        default_policy_set_id: form.default_policy_set_id
          ? Number(form.default_policy_set_id)
          : null,
      };
      const saved = await saveRentalSettings(payload, token);
      const nextForm = { ...defaultSettings, ...(saved || {}) };
      setForm(nextForm);
      publishRentalsFeatureSnapshot({
        enabled: true,
        settingsReady: Boolean(nextForm.default_salon_id && nextForm.default_policy_set_id),
        operationalReady: Boolean(nextForm.default_salon_id && nextForm.default_policy_set_id),
        rentalMode: nextForm.rental_mode,
        discoverability: nextForm.discoverability,
        marketplaceEnabled: Boolean(nextForm.marketplace_enabled),
        defaultSalonId: nextForm.default_salon_id ?? null,
        defaultPolicySetId: nextForm.default_policy_set_id ?? null,
        defaultBookingMode: nextForm.default_booking_mode || "internal",
        defaultRequiresApproval: Boolean(nextForm.default_requires_approval),
      });
      await rentalsFeature.refresh();
      setFlash("Rental settings saved.");
    } catch (err) {
      setError(err.message || "Unable to save rental settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <RentalsShell
      title="Rental Settings"
      description="Manage the business-level defaults that drive workspace publication, approval behavior, and marketplace readiness from MyBoothShare."
      actions={
        <div className="flex flex-wrap gap-3">
          <Link
            to={getMarketplaceOperatorWorkspacesPath()}
            className={rentalsHeroSecondaryActionClass}
          >
            Open Inventory
          </Link>
          <Link
            to={getMarketplaceOperatorHubPath()}
            className={rentalsHeroSecondaryActionClass}
          >
            Back to Rentals
          </Link>
        </div>
      }
    >
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        {summaryCards.map((card) => (
          <SummaryChip
            key={card.label}
            label={card.label}
            value={card.value}
            tone={card.tone}
          />
        ))}
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <SectionCard
          title="Operational Defaults"
          description="These settings define how rentals behave by default for new workspaces and how the marketplace is allowed to surface them."
        >
          {loading ? <p className="text-sm text-slate-600">Loading settings...</p> : null}
          <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
            <InputLabel label="Rental mode">
              <select
                className={fieldClass}
                value={form.rental_mode}
                onChange={(event) => updateField("rental_mode", event.target.value)}
              >
                <option value="internal">Internal only</option>
                <option value="invite_only">Invite only</option>
                <option value="public">Public marketplace</option>
              </select>
            </InputLabel>
            <InputLabel label="Discoverability">
              <select
                className={fieldClass}
                value={form.discoverability}
                onChange={(event) =>
                  updateField("discoverability", event.target.value)
                }
              >
                <option value="private">Private</option>
                <option value="invite_only">Invite only</option>
                <option value="public">Public</option>
              </select>
            </InputLabel>
            <InputLabel label="Default rental request mode">
              <select
                className={fieldClass}
                value={form.default_booking_mode}
                onChange={(event) =>
                  updateField("default_booking_mode", event.target.value)
                }
              >
                <option value="internal">Internal</option>
                <option value="request">Request</option>
                <option value="instant">Instant</option>
                <option value="invite_only">Invite only</option>
              </select>
            </InputLabel>
            <InputLabel
              label="Default policy set"
              hint="Use the seeded workspace policy set ID from the backend until Phase 4 adds full policy selection tooling."
            >
              <input
                className={fieldClass}
                type="number"
                min="1"
                value={form.default_policy_set_id ?? ""}
                onChange={(event) =>
                  updateField("default_policy_set_id", event.target.value)
                }
                placeholder="Policy set ID"
              />
            </InputLabel>
            <InputLabel label="Default salon ID">
              <input
                className={fieldClass}
                type="number"
                min="1"
                value={form.default_salon_id ?? ""}
                onChange={(event) =>
                  updateField("default_salon_id", event.target.value)
                }
                placeholder="Salon ID"
              />
            </InputLabel>
            <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:col-span-2 xl:col-span-3">
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-5 text-slate-700">
                  <input
                    className="mt-0.5 shrink-0"
                    type="checkbox"
                    checked={Boolean(form.marketplace_enabled)}
                    onChange={(event) =>
                      updateField("marketplace_enabled", event.target.checked)
                    }
                  />
                  <span className="min-w-0 break-words">Enable marketplace surface</span>
                </label>
                <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-5 text-slate-700">
                  <input
                    className="mt-0.5 shrink-0"
                    type="checkbox"
                    checked={Boolean(form.allow_public_checkout)}
                    onChange={(event) =>
                      updateField("allow_public_checkout", event.target.checked)
                    }
                  />
                  <span className="min-w-0 break-words">Allow public checkout</span>
                </label>
                <label className="flex min-w-0 items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium leading-5 text-slate-700">
                  <input
                    className="mt-0.5 shrink-0"
                    type="checkbox"
                    checked={Boolean(form.default_requires_approval)}
                    onChange={(event) =>
                      updateField("default_requires_approval", event.target.checked)
                    }
                  />
                  <span className="min-w-0 break-words">Require approval by default</span>
                </label>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Support And Brand Surface"
          description="Keep the MyBoothShare surface inside the Ellyanna product family while still giving the public marketplace a dedicated voice."
        >
          <div className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
            <InputLabel label="Support email">
              <input
                className={fieldClass}
                type="email"
                value={form.support_email || ""}
                onChange={(event) =>
                  updateField("support_email", event.target.value)
                }
              />
            </InputLabel>
            <InputLabel label="Support phone">
              <input
                className={fieldClass}
                value={form.support_phone || ""}
                onChange={(event) =>
                  updateField("support_phone", event.target.value)
                }
              />
            </InputLabel>
            <InputLabel label="Public brand label">
              <input
                className={fieldClass}
                value={form.public_brand_label || ""}
                onChange={(event) =>
                  updateField("public_brand_label", event.target.value)
                }
              />
            </InputLabel>
            <InputLabel label="Hero headline">
              <input
                className={fieldClass}
                value={form.hero_headline || ""}
                onChange={(event) =>
                  updateField("hero_headline", event.target.value)
                }
              />
            </InputLabel>
            <div className="min-w-0 md:col-span-2">
              <InputLabel label="Hero subheadline">
              <textarea
                className={textareaClass}
                value={form.hero_subheadline || ""}
                onChange={(event) =>
                  updateField("hero_subheadline", event.target.value)
                }
              />
              </InputLabel>
            </div>
          </div>
        </SectionCard>

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={saving || loading}
            className="rounded-full bg-rose-500 px-5 py-2.5 text-center text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Rental Settings"}
          </button>
          <Link
            to={getMarketplaceOperatorWorkspaceCreatePath()}
            className="rounded-full border border-slate-300 px-5 py-2.5 text-center text-sm font-semibold text-slate-700 hover:border-slate-400"
          >
            List a Workspace
          </Link>
        </div>
      </form>
    </RentalsShell>
  );
}
